// Card utility functions - supports multiple game types

import { getGameType } from './gameTypes';

// Ball colors (for backwards compatibility)
const BALL_COLORS = {
  1: '#FFD700',  // Yellow
  2: '#1E3A8A',  // Blue
  3: '#DC2626',  // Red
  4: '#6B21A8',  // Purple
  5: '#EA580C',  // Orange
  6: '#15803D',  // Green
  7: '#7C2D12',  // Maroon/Brown
  8: '#1a1a2e',  // Black
  9: '#FFD700',  // Yellow stripe
  10: '#1E3A8A', // Blue stripe
  11: '#DC2626', // Red stripe
  12: '#6B21A8', // Purple stripe
  13: '#EA580C', // Orange stripe
  14: '#15803D', // Green stripe
  15: '#7C2D12', // Maroon stripe
};

/**
 * Generate deck for a specific game type
 * @param {string} gameTypeId - Game type ID
 * @param {boolean} allowDuplicates - Allow duplicate cards
 * @param {number} copies - Number of copies when duplicates allowed
 */
export const generateDeck = (gameTypeId = 'billiards', allowDuplicates = true, copies = 4) => {
  const gameType = getGameType(gameTypeId);
  return gameType.generateCards(allowDuplicates, copies);
};

/**
 * Shuffle array using Fisher-Yates algorithm
 */
export const shuffleDeck = (deck) => {
  const shuffled = [...deck];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

/**
 * Distribute cards equally among players
 * @param {number} playerCount - Number of players
 * @param {number} cardsPerPlayer - Cards per player (optional)
 * @param {string} gameTypeId - Game type ID
 * @param {boolean} allowDuplicates - Allow duplicate numbers in deck
 */
export const distributeCards = (playerCount, cardsPerPlayer = null, gameTypeId = 'billiards', allowDuplicates = true) => {
  const gameType = getGameType(gameTypeId);
  const deck = shuffleDeck(generateDeck(gameTypeId, allowDuplicates, gameType.defaultCopies));
  
  // For Exploding Kittens, ensure each player gets a defuse card
  if (gameTypeId === 'explodingKittens') {
    const defuseCards = deck.filter(c => c.type === 'defuse');
    const otherCards = deck.filter(c => c.type !== 'defuse' && c.type !== 'exploding_kitten');
    
    // Shuffle other cards
    const shuffledOther = shuffleDeck(otherCards);
    
    const defaultCardsPerPlayer = cardsPerPlayer || gameType.defaultCardsPerPlayer;
    const hands = [];
    
    for (let i = 0; i < playerCount; i++) {
      const hand = [];
      // Give each player one defuse card
      if (defuseCards.length > i) {
        hand.push(defuseCards[i]);
      }
      // Fill rest with other cards (excluding exploding kittens)
      const otherCardsNeeded = defaultCardsPerPlayer - hand.length;
      const startIdx = i * otherCardsNeeded;
      for (let j = 0; j < otherCardsNeeded && startIdx + j < shuffledOther.length; j++) {
        hand.push(shuffledOther[startIdx + j]);
      }
      hands.push(hand);
    }
    
    return hands;
  }
  
  // Standard distribution for other games
  const defaultCardsPerPlayer = cardsPerPlayer || gameType.defaultCardsPerPlayer;
  const maxCards = Math.min(defaultCardsPerPlayer, Math.floor(deck.length / playerCount));
  
  const hands = [];
  for (let i = 0; i < playerCount; i++) {
    hands.push(deck.slice(i * maxCards, (i + 1) * maxCards));
  }
  
  return hands;
};

/**
 * Remove a random card from a hand
 */
export const removeRandomCard = (cards) => {
  if (cards.length === 0) return { remainingCards: [], removedCard: null };
  
  const randomIndex = Math.floor(Math.random() * cards.length);
  const removedCard = cards[randomIndex];
  const remainingCards = cards.filter((_, index) => index !== randomIndex);
  
  return { remainingCards, removedCard };
};

/**
 * Generate a unique game ID
 */
export const generateGameId = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let id = '';
  for (let i = 0; i < 6; i++) {
    id += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return id;
};

/**
 * Generate a unique player ID
 */
export const generatePlayerId = () => {
  return `player_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

export { BALL_COLORS };

